import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ImageDown, Sparkles } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import OnboardingTour from "@/components/OnboardingTour";
import CircleOfTheWeekCard from "@/components/CircleOfTheWeekCard";
import TransactionsInbox from "@/components/TransactionsInbox";
import { useAuth } from "@/contexts/AuthContext";
import useDashboardData from "@/hooks/useDashboardData";
import EmptyDashboardCTA from "@/components/EmptyDashboardCTA";
import TrialEndingModal from "@/components/TrialEndingModal";
import InlineUpgradePill from "@/components/InlineUpgradePill";
import WhatsNewModal from "@/components/WhatsNewModal";
import NudgeCard from "@/components/dashboard/NudgeCard";
import StreakBanner from "@/components/dashboard/StreakBanner";
import OverviewGrid, { MonthlyStatRow } from "@/components/dashboard/OverviewGrid";
import ChartsRow from "@/components/dashboard/ChartsRow";
import OpportunitiesAndBills from "@/components/dashboard/OpportunitiesAndBills";
import SpendingShiftCard from "@/components/dashboard/SpendingShiftCard";
import SubscriptionCard from "@/components/billing/SubscriptionCard";

// Progressive reveal — the below-the-fold "delight" cards ship as their
// own JS chunks so the main Dashboard render isn't blocked on their
// bundles. Suspense keeps their layout slot empty (rather than jumping)
// while their chunk is being fetched.
const SproutMomentumCard = lazy(() => import("@/components/dashboard/SproutMomentumCard"));
const PortfolioUpdateCard = lazy(() => import("@/components/dashboard/PortfolioUpdateCard"));
const MonthlySnapshotModal = lazy(() => import("@/components/MonthlySnapshotModal"));

/**
 * Dashboard — top-level composition only. All data lives in
 * `useDashboardData`, each visual section is its own sub-component in
 * `components/dashboard/*.jsx`. Refactored in iter-31 (was 606 lines, now
 * a thin composition of ~90 lines).
 */
export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const {
    summary, cashflow, breakdown, insights, streak,
    nudge, nudgeDismissed, nudgeRefreshing,
    tourSteps, circleWeek, moments, portfolioUpdate,
    loading, dismissNudge, refreshNudge,
  } = useDashboardData();

  if (loading || !summary) {
    return <div className="text-sm text-[var(--text-muted)]">{t("common.loading_dashboard")}</div>;
  }

  const cur = user?.currency || "USD";

  return (
    <div>
      <TrialEndingModal />
      <WhatsNewModal />
      <InlineUpgradePill />
      <PageHeader
        subtitle={t("pages.dashboard.hello", { name: user?.name?.split(" ")[0] || "friend" })}
        title={t("pages.dashboard.title")}
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSnapshotOpen(true)}
              className="btn-ghost"
              data-testid="dashboard-snapshot-btn"
              title={t("dashboard_body.screenshot_hint")}
            >
              <ImageDown size={16}/> {t("dashboard_body.screenshot")}
            </button>
            <Link to="/wrapup" className="btn-ghost" data-testid="dashboard-wrapup-btn">
              <Sparkles size={16}/> {t("dashboard_body.wrapup")}
            </Link>
            <Link to="/advisor" className="btn-primary" data-testid="dashboard-ask-ai-btn">
              <Sparkles size={16}/> {t("dashboard_body.ask_ai")}
            </Link>
          </div>
        }
      />

      {nudge && !nudgeDismissed && (
        <NudgeCard
          nudge={nudge}
          onDismiss={dismissNudge}
          onRefresh={refreshNudge}
          refreshing={nudgeRefreshing}
        />
      )}

      {(!summary.monthly_income || summary.monthly_income === 0) &&
       (!summary.monthly_expense || summary.monthly_expense === 0) && (
        <EmptyDashboardCTA userName={user?.name} />
      )}

      <OverviewGrid summary={summary} currency={cur} />

      <SpendingShiftCard currency={cur} />

      <div className="mb-6">
        <SubscriptionCard variant="dashboard" />
      </div>

      <StreakBanner streak={streak} />

      <CircleOfTheWeekCard data={circleWeek} />

      <TransactionsInbox currency={cur} />

      <Suspense fallback={null}>
        <SproutMomentumCard moments={moments} currency={cur} />
        <PortfolioUpdateCard update={portfolioUpdate} currency={cur} />
      </Suspense>

      <MonthlyStatRow summary={summary} currency={cur} />

      <ChartsRow cashflow={cashflow} breakdown={breakdown} currency={cur} />

      <OpportunitiesAndBills
        insights={insights}
        upcomingBills={summary.upcoming_bills}
        currency={cur}
      />

      {tourSteps && tourSteps.length > 0 && (
        <OnboardingTour steps={tourSteps} storageKey="pfo_tour_completed" />
      )}

      <Suspense fallback={null}>
        <MonthlySnapshotModal
          open={snapshotOpen}
          onClose={() => setSnapshotOpen(false)}
          summary={summary}
          streakCount={streak?.current_streak || 0}
          topInsight={Array.isArray(insights?.insights) && insights.insights.length > 0 ? insights.insights[0] : null}
          currency={cur}
          userName={user?.name}
        />
      </Suspense>
    </div>
  );
}
